import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'shimmer' | 'none';
}

/**
 * Base Skeleton component with animated placeholder
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'rectangular',
  width,
  height,
  animation = 'shimmer',
}) => {
  const baseClasses = 'bg-gray-200 dark:bg-gray-700';

  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  const animationClasses = {
    pulse: 'animate-pulse',
    shimmer: 'animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 bg-[length:200%_100%]',
    none: '',
  };

  const style: React.CSSProperties = {
    width: width,
    height: height,
  };

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${animationClasses[animation]} ${className}`}
      style={style}
      role="presentation"
      aria-hidden="true"
    />
  );
};

/**
 * Skeleton for text lines
 */
export const SkeletonText: React.FC<{
  lines?: number;
  className?: string;
  lastLineWidth?: string;
}> = ({ lines = 3, className = '', lastLineWidth = '60%' }) => {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          variant="text"
          height={16}
          width={index === lines - 1 ? lastLineWidth : '100%'}
        />
      ))}
    </div>
  );
};

/**
 * Skeleton for stat cards (used in Dashboard and DocumentUploads)
 */
export const SkeletonStatCard: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`p-4 rounded-lg bg-gray-100 dark:bg-storgia-dark ${className}`}>
      <Skeleton variant="text" height={14} width="60%" className="mb-2" />
      <Skeleton variant="text" height={32} width="40%" />
    </div>
  );
};

/**
 * Skeleton for table rows
 */
export const SkeletonTableRow: React.FC<{ columns?: number }> = ({ columns = 6 }) => {
  return (
    <tr className="animate-pulse">
      {Array.from({ length: columns }).map((_, index) => (
        <td key={index} className="px-6 py-4">
          <Skeleton variant="text" height={16} width={index === 0 ? '80%' : '60%'} />
          {index === 0 && (
            <Skeleton variant="text" height={12} width="50%" className="mt-1" />
          )}
        </td>
      ))}
    </tr>
  );
};

/**
 * Skeleton for document upload cards (mobile view)
 */
export const SkeletonUploadCard: React.FC = () => {
  return (
    <div className="p-4 rounded-lg border bg-white dark:bg-storgia-dark border-gray-200 dark:border-storgia-light-blurple/20">
      <div className="flex justify-between items-start mb-3">
        <div className="space-y-2">
          <Skeleton variant="text" height={18} width={120} />
          <Skeleton variant="text" height={14} width={180} />
        </div>
        <Skeleton variant="rectangular" height={24} width={60} />
      </div>
      <div className="flex gap-4 mb-3">
        <Skeleton variant="text" height={14} width={80} />
        <Skeleton variant="text" height={14} width={80} />
      </div>
      <div className="pt-3 border-t border-gray-100 dark:border-storgia-light-blurple/20 flex justify-between">
        <Skeleton variant="text" height={14} width={100} />
        <Skeleton variant="text" height={14} width={80} />
      </div>
    </div>
  );
};

/**
 * Skeleton for the dashboard summary card
 */
export const SkeletonDashboardSummary: React.FC = () => {
  return (
    <div className="bg-gradient-to-r from-gray-300 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-2xl p-5 animate-pulse mb-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <Skeleton variant="text" height={14} width={140} className="mb-2 opacity-70" />
          <Skeleton variant="text" height={36} width={120} />
        </div>
      </div>
      <div className="flex gap-4 pt-4 border-t border-gray-400/30 dark:border-gray-500/30">
        <div>
          <Skeleton variant="text" height={12} width={80} className="mb-1 opacity-70" />
          <Skeleton variant="text" height={24} width={40} />
        </div>
        <div>
          <Skeleton variant="text" height={12} width={80} className="mb-1 opacity-70" />
          <Skeleton variant="text" height={24} width={40} />
        </div>
      </div>
    </div>
  );
};

/**
 * Skeleton for charts
 */
export const SkeletonChart: React.FC<{ height?: number; className?: string }> = ({
  height = 200,
  className = ''
}) => {
  return (
    <div className={`rounded-2xl p-5 bg-white dark:bg-storgia-dark border border-gray-200 dark:border-storgia-light-blurple/20 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" height={18} width={140} />
        </div>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" height={24} width={40} />
          ))}
        </div>
      </div>
      <div style={{ height }} className="flex items-end gap-1">
        {/* Deterministic heights for chart skeleton bars */}
        {[45, 65, 35, 80, 55, 70, 40, 60, 75, 50, 30, 85].map((heightPercent, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            width="8%"
            height={`${heightPercent}%`}
            className="rounded-t"
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Skeleton for activity/recent uploads list items
 */
export const SkeletonActivityItem: React.FC = () => {
  return (
    <div className="p-4 rounded-xl border bg-white dark:bg-storgia-dark border-gray-200 dark:border-storgia-light-blurple/20">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton variant="rectangular" width={40} height={40} />
          <div>
            <Skeleton variant="text" height={16} width={140} className="mb-1" />
            <Skeleton variant="text" height={12} width={180} />
          </div>
        </div>
        <div className="text-right mr-4">
          <Skeleton variant="text" height={14} width={100} className="mb-1" />
          <Skeleton variant="text" height={12} width={80} />
        </div>
        <Skeleton variant="circular" width={20} height={20} />
      </div>
    </div>
  );
};

/**
 * Skeleton for complex cards
 */
export const SkeletonComplexCard: React.FC = () => {
  return (
    <div className="p-4 rounded-xl border bg-white dark:bg-storgia-dark border-gray-200 dark:border-storgia-light-blurple/20">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Skeleton variant="text" height={18} width="60%" className="mb-2" />
          <Skeleton variant="text" height={14} width="80%" className="mb-3" />
          <div className="flex gap-4">
            <Skeleton variant="text" height={12} width={60} />
            <Skeleton variant="text" height={12} width={80} />
          </div>
        </div>
        <Skeleton variant="circular" width={24} height={24} />
      </div>
    </div>
  );
};

/**
 * Skeleton for ComplexDetails stats grid
 */
export const SkeletonComplexStats: React.FC = () => {
  return (
    <div className="grid grid-cols-2 gap-4">
      {[1, 2].map((i) => (
        <div key={i} className="p-4 rounded-xl border shadow-sm bg-white dark:bg-storgia-dark border-gray-100 dark:border-storgia-light-blurple/20">
          <div className="flex items-center text-xs mb-2">
            <Skeleton variant="circular" width={14} height={14} className="mr-1.5" />
            <Skeleton variant="text" height={12} width={60} />
            <Skeleton variant="rectangular" height={16} width={30} className="ml-auto" />
          </div>
          <Skeleton variant="text" height={32} width="50%" className="mb-1" />
          <Skeleton variant="text" height={12} width="70%" />
        </div>
      ))}
    </div>
  );
};

/**
 * Skeleton for activity log items in ComplexDetails
 */
export const SkeletonActivityLogItem: React.FC = () => {
  return (
    <div className="p-4 flex items-center justify-between">
      <div className="min-w-0 flex-1 mr-4">
        <div className="flex items-center mb-1">
          <Skeleton variant="text" height={16} width={120} className="mr-2" />
          <Skeleton variant="rectangular" height={18} width={70} />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton variant="text" height={12} width={100} />
          <Skeleton variant="text" height={12} width={60} />
        </div>
      </div>
      <div className="text-right">
        <Skeleton variant="text" height={16} width={50} />
      </div>
    </div>
  );
};

export default Skeleton;
